import { useEffect, useState, type FormEvent } from "react";
import { usePostHog } from "posthog-js/react";
import { usePageMeta } from "../usePageMeta";
import { StudioLink } from "../components/StudioLink";

import {
  STUDIO_DESCRIPTION,
  STUDIO_FAQ,
  STUDIO_FEATURES,
  STUDIO_HEADING,
  STUDIO_INTRO,
  STUDIO_PAGE_PATH,
  STUDIO_STRUCTURED_DATA,
  STUDIO_TITLE,
} from "../studioCopy";

interface Template {
  name: string;
  url: string;
  images: { card: { width: number; height: number; webp: string; png: string } } | null;
}

/**
 * Stitch Studio's landing page, at /studio. Also served, unchanged, at
 * /studiomobile: that is where Studio's phone notice sends someone who taps
 * "I'm interested, send me more info" (embroidery-desktop), so it can't
 * move. It isn't redirected either, because the PostHog funnel
 * studio_mobile_interest_clicked -> $pageview on /studiomobile ->
 * studio_mobile_signup_submitted needs that pageview's URL; its canonical is
 * /studio instead.
 *
 * The email form (app/routers/studio_link.py) is for phone visitors, so on a
 * phone it sits right under the hero, and from /studiomobile it leads.
 */
export function StudioPage({ apiUrl, fromMobile }: { apiUrl: string; fromMobile: boolean }) {
  usePageMeta({
    title: STUDIO_TITLE,
    description: STUDIO_DESCRIPTION,
    path: STUDIO_PAGE_PATH,
    structuredData: STUDIO_STRUCTURED_DATA,
  });
  const [templates, setTemplates] = useState<Template[]>([]);

  useEffect(() => {
    let cancelled = false;
    fetch(`${apiUrl}/api/templates`)
      .then((res) => (res.ok ? res.json() : { templates: [] }))
      .then((data: { templates: Template[] }) => {
        // Only the ones with a rendered card: a grid of blank tiles is worse
        // than no grid.
        if (!cancelled) setTemplates(data.templates.filter((t) => t.images));
      })
      .catch(() => {
        // The page stands without them.
      });
    return () => {
      cancelled = true;
    };
  }, [apiUrl]);

  return (
    <div className={`studio-page${fromMobile ? " studio-page-from-mobile" : ""}`}>
      <section className="studio-hero">
        <p className="studio-eyebrow">
          Stitch Studio <span className="nav-badge">Beta</span>
        </p>
        <h1>{STUDIO_HEADING}</h1>
        <p className="studio-lede">{STUDIO_INTRO}</p>
        <StudioLink source="studio_page" className="cta-button">
          Open Stitch Studio →
        </StudioLink>
        <p className="studio-formats">Exports PES · DST · JEF · EXP · VP3</p>
      </section>

      <section id="studio-email" className="studio-email" aria-labelledby="studio-email-heading">
        <StudioEmailForm apiUrl={apiUrl} fromMobile={fromMobile} />
      </section>

      <section className="studio-features" aria-labelledby="studio-features-heading">
        <h2 id="studio-features-heading">What you can do</h2>
        <div className="studio-features-grid">
          {STUDIO_FEATURES.map((f) => (
            <div key={f.title} className="studio-feature">
              <h3>{f.title}</h3>
              <p>{f.body}</p>
            </div>
          ))}
        </div>
      </section>

      {templates.length > 0 && (
        <section className="studio-templates" aria-labelledby="studio-templates-heading">
          <h2 id="studio-templates-heading">Free patch templates</h2>
          <p className="studio-section-lede">Pick one and it opens in Stitch Studio, ready to change and export.</p>
          <div className="studio-templates-grid">
            {templates.map((t) => (
              <StudioLink
                key={t.url}
                source="studio_page_template"
                importFile={{ url: t.url, name: t.name }}
                className="studio-template"
              >
                <picture>
                  <source srcSet={t.images!.card.webp} type="image/webp" />
                  <img
                    src={t.images!.card.png}
                    width={t.images!.card.width}
                    height={t.images!.card.height}
                    alt={`${t.name} embroidery design`}
                    loading="lazy"
                  />
                </picture>
                <span className="studio-template-name">{t.name}</span>
              </StudioLink>
            ))}
          </div>
        </section>
      )}

      <section className="studio-closing">
        <h2>Ready to make something?</h2>
        <StudioLink source="studio_page_footer" className="cta-button">
          Open Stitch Studio →
        </StudioLink>
      </section>

      <section className="studio-faq" aria-labelledby="studio-faq-heading">
        <h2 id="studio-faq-heading">Questions</h2>
        {STUDIO_FAQ.map((faq) => (
          <details key={faq.question}>
            <summary>{faq.question}</summary>
            <p>{faq.answer}</p>
          </details>
        ))}
      </section>
    </div>
  );
}

/**
 * "Email me the link", posting to /api/studio-link. The link email is sent
 * whatever the box says; the box only decides whether the address joins the
 * studio_mobile_opt_in campaign source.
 *
 * Studio already sends studio_mobile_interest_clicked; this adds
 * studio_mobile_signup_submitted, never with the address. `page` tells the
 * two ways in apart.
 */
function StudioEmailForm({ apiUrl, fromMobile }: { apiUrl: string; fromMobile: boolean }) {
  const posthog = usePostHog();
  const [email, setEmail] = useState("");
  const [optIn, setOptIn] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sentTo, setSentTo] = useState<string | null>(null);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const res = await fetch(`${apiUrl}/api/studio-link`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, marketing_opt_in: optIn }),
      });
      if (!res.ok) {
        let detail = `Something went wrong (HTTP ${res.status}). Please try again.`;
        try {
          const body = await res.json();
          if (typeof body.detail === "string") detail = body.detail;
        } catch {
          // Not JSON: keep the generic message.
        }
        setError(detail);
        return;
      }
      posthog?.capture("studio_mobile_signup_submitted", {
        marketing_opt_in: optIn,
        page: fromMobile ? "studiomobile" : "studio",
      });
      setSentTo(email.trim());
    } catch {
      setError("We couldn't reach the server. Check your connection and try again.");
    } finally {
      setBusy(false);
    }
  };

  if (sentTo) {
    return (
      <article className="studio-email-card" role="status">
        <h2 id="studio-email-heading">Check your inbox</h2>
        <p>
          We've sent your Stitch Studio link to <strong>{sentTo}</strong>. Open it on your computer whenever you're
          ready.
        </p>
        <p>
          <small>Not there? Check your spam folder.</small>
        </p>
      </article>
    );
  }

  return (
    <article className="studio-email-card">
      {/* From Studio's phone notice this leads the page, so it says what it is. */}
      <h2 id="studio-email-heading">
        {fromMobile ? "Send Stitch Studio to your computer" : "Not at your computer? Save it for later"}
      </h2>
      <p>
        Stitch Studio is built for a big screen and a mouse. Leave your email and we'll send you a link to open on
        your computer.
      </p>
      <form onSubmit={submit}>
        <label>
          Email
          <input
            type="email"
            name="email"
            autoComplete="email"
            inputMode="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={busy}
          />
        </label>
        <label>
          <input
            type="checkbox"
            name="marketing_opt_in"
            checked={optIn}
            onChange={(e) => setOptIn(e.target.checked)}
            disabled={busy}
          />
          Also send me occasional updates: new Studio features, free templates and tips. Unsubscribe anytime.
        </label>
        {error && (
          <p role="alert" style={{ color: "var(--pico-del-color, #b3261e)" }}>
            {error}
          </p>
        )}
        <button type="submit" disabled={busy} aria-busy={busy}>
          Email me the link
        </button>
      </form>
      <p>
        <small>We'll only use your email to send this link, and for updates if you tick the box.</small>
      </p>
    </article>
  );
}
